import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type RecipientRow = {
  id: string;
  name: string;
  legal_name: string | null;
  card_nickname: string | null;
  phone: string | null;
  relationship: string;
  birthday: string | null;
  anniversary: string | null;
  favorite_flower: string | null;
  reminder_days: number;
  reminder_days_set: number[];
  gift_lane: string | null;
  inactivity_nudge_days: number | null;
  hero_protocol: boolean;
  hero_frequency: "low" | "medium" | "high" | null;
  hero_note: string | null;
  gift_preferences: string[];
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  event_type: string | null;
  vibe: string | null;
  autopilot_enabled: boolean;
  style_preference: string | null;
  style_prefs: string[];
  color_palette: string | null;
  recipient_type: string | null;
  age_group: string | null;
  interest_tags: string[];
  default_card_note: string | null;
  is_primary: boolean;
  created_at: string;
};


export type UpsertRecipientInput = {
  id?: string;
  name: string;
  legalName?: string | null;
  cardNickname?: string | null;
  phone?: string | null;
  relationship: string;
  birthday?: string | null;
  anniversary?: string | null;
  favoriteFlower?: string | null;
  reminderDays?: 3 | 7 | 14;
  reminderDaysSet?: number[] | null;
  giftLane?: string | null;
  inactivityNudgeDays?: number | null;
  heroProtocol?: boolean;
  heroFrequency?: "low" | "medium" | "high" | null;
  heroNote?: string | null;
  giftPreferences: string[];
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  eventType?: "birthday" | "anniversary" | "other" | null;
  vibe?: "classic" | "modern" | "bright" | "dealers_choice" | null;
  autopilotEnabled?: boolean;
  stylePreference?: string | null;
  stylePrefs?: string[] | null;
  colorPalette?: string | null;
  recipientType?: string | null;
  ageGroup?: string | null;
  interestTags?: string[] | null;
  defaultCardNote?: string | null;
  isPrimary?: boolean;
};

const clamp = (s: string | null | undefined, n: number) =>
  typeof s === "string" ? s.slice(0, n) : null;
const optDate = (s: string | null | undefined) =>
  s && /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;

const ALLOWED_PREFS = new Set(["flowers", "perfume", "jewelry", "mens"]);
const ALLOWED_VIBE = new Set(["classic", "modern", "bright", "dealers_choice"]);
const ALLOWED_EVENT = new Set(["birthday", "anniversary", "other"]);
const ALLOWED_STYLE = new Set(["classic", "modern", "wildflower", "minimal"]);
const ALLOWED_STYLE_PREFS = new Set([
  "modern_luxury",
  "soft_romantic",
  "artisanal_boho",
  "spa_pampering",
  "classic_elegance",
]);
const ALLOWED_COLOR = new Set([
  "deep_reds",
  "soft_pastels",
  "earthy_neutrals",
  "vibrant_bold",
]);
const ALLOWED_AGE_GROUP = new Set(["adult", "teen", "kid"]);
const ALLOWED_INTERESTS = new Set([
  "florals", "spa", "jewelry", "gourmet", "toys",
  "books", "tech", "fashion", "wellness", "home",
]);
const ALLOWED_RECIPIENT_TYPE = new Set(["wife", "partner", "mother", "other"]);
const ALLOWED_GIFT_LANES = new Set([
  "florals",
  "curated_gift",
  "local_coffee",
  "local_lunch",
  "egift",
]);

const SELECT_COLS =
  "id, name, legal_name, card_nickname, phone, relationship, birthday, anniversary, favorite_flower, reminder_days, reminder_days_set, gift_lane, inactivity_nudge_days, hero_protocol, hero_frequency, hero_note, gift_preferences, address, city, state, zip, event_type, vibe, autopilot_enabled, style_preference, style_prefs, color_palette, recipient_type, age_group, interest_tags, default_card_note, is_primary, created_at";

export const listRecipients = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<RecipientRow[]> => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("recipients")
      .select(SELECT_COLS)
      .eq("user_id", userId)
      .order("is_primary", { ascending: false })
      .order("created_at", { ascending: true });
    if (error) throw new Error("Failed to load recipients");
    return (data ?? []) as RecipientRow[];
  });

export const upsertRecipient = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: UpsertRecipientInput) => {
    if (!data || typeof data !== "object") throw new Error("Invalid payload");
    if (!data.name || typeof data.name !== "string") throw new Error("Name required");
    if (!data.relationship || typeof data.relationship !== "string")
      throw new Error("Relationship required");
    const reminderSet = Array.isArray(data.reminderDaysSet)
      ? Array.from(
          new Set(data.reminderDaysSet.filter((n) => [1, 3, 7, 14].includes(Number(n))).map(Number)),
        ).sort((a, b) => b - a)
      : [14, 7, 3];
    const reminderDays = ([3, 7, 14].includes(Number(data.reminderDays))
      ? Number(data.reminderDays)
      : Math.max(...reminderSet.filter((n) => n !== 1), 7)) as 3 | 7 | 14;
    const giftLane =
      data.giftLane && ALLOWED_GIFT_LANES.has(data.giftLane) ? data.giftLane : null;
    const inactivityNudgeDays = [30, 60, 90].includes(Number(data.inactivityNudgeDays))
      ? Number(data.inactivityNudgeDays)
      : null;
    if (data.heroFrequency && !["low", "medium", "high"].includes(data.heroFrequency))
      throw new Error("Invalid frequency");
    const prefs = Array.isArray(data.giftPreferences)
      ? data.giftPreferences.filter((p) => typeof p === "string" && ALLOWED_PREFS.has(p))
      : [];
    const vibe = data.vibe && ALLOWED_VIBE.has(data.vibe) ? data.vibe : null;
    const eventType = data.eventType && ALLOWED_EVENT.has(data.eventType) ? data.eventType : null;
    const stylePreference =
      data.stylePreference && ALLOWED_STYLE.has(data.stylePreference) ? data.stylePreference : null;
    const stylePrefs = Array.isArray(data.stylePrefs)
      ? data.stylePrefs.filter((s) => typeof s === "string" && ALLOWED_STYLE_PREFS.has(s)).slice(0, 2)
      : [];
    const colorPalette =
      data.colorPalette && ALLOWED_COLOR.has(data.colorPalette) ? data.colorPalette : null;
    const recipientType =
      data.recipientType && ALLOWED_RECIPIENT_TYPE.has(data.recipientType) ? data.recipientType : null;
    const ageGroup = data.ageGroup && ALLOWED_AGE_GROUP.has(data.ageGroup) ? data.ageGroup : null;
    const interestTags = Array.isArray(data.interestTags)
      ? data.interestTags.filter((t) => typeof t === "string" && ALLOWED_INTERESTS.has(t)).slice(0, 10)
      : [];
    return {
      ...data,
      reminderDays,
      reminderDaysSet: reminderSet,
      giftLane,
      inactivityNudgeDays,
      ageGroup,
      interestTags,
      giftPreferences: prefs.length ? prefs : ["flowers"],
      vibe,
      eventType,
      stylePreference,
      stylePrefs,
      colorPalette,
      recipientType,
    };
  })
  .handler(async ({ data, context }): Promise<RecipientRow> => {
    const { supabase, userId } = context;

    // Auto-set is_primary=true on the very first recipient a member creates,
    // unless they explicitly set otherwise or the flag is being managed here.
    let resolvedIsPrimary = data.isPrimary;
    if (!data.id && typeof resolvedIsPrimary !== "boolean") {
      const { count } = await supabase
        .from("recipients")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId);
      resolvedIsPrimary = (count ?? 0) === 0;
    }

    // If marking this recipient primary, demote any other primary first
    // (unique index would otherwise reject the write).
    if (resolvedIsPrimary === true) {
      await supabase
        .from("recipients")
        .update({ is_primary: false })
        .eq("user_id", userId)
        .eq("is_primary", true);
    }

    const payload = {
      user_id: userId,
      name: clamp(data.name, 120)!,
      legal_name: clamp(data.legalName, 160),
      card_nickname: clamp(data.cardNickname, 80),
      phone: clamp(data.phone, 32),
      relationship: clamp(data.relationship, 80)!,
      birthday: optDate(data.birthday),
      anniversary: optDate(data.anniversary),
      favorite_flower: clamp(data.favoriteFlower, 500),
      reminder_days: data.reminderDays,
      reminder_days_set: data.reminderDaysSet,
      gift_lane: data.giftLane,
      inactivity_nudge_days: data.inactivityNudgeDays,
      hero_protocol: !!data.heroProtocol,
      hero_frequency: data.heroProtocol ? data.heroFrequency ?? "medium" : null,
      hero_note: clamp(data.heroNote, 240),
      gift_preferences: data.giftPreferences,
      address: clamp(data.address, 200),
      city: clamp(data.city, 80),
      state: clamp(data.state, 40),
      zip: clamp(data.zip, 20),
      event_type: data.eventType ?? null,
      vibe: data.vibe ?? null,
      autopilot_enabled: data.autopilotEnabled ?? true,
      style_preference: data.stylePreference ?? null,
      style_prefs: (data as any).stylePrefs ?? [],
      color_palette: (data as any).colorPalette ?? null,
      recipient_type: (data as any).recipientType ?? null,
      age_group: (data as any).ageGroup ?? null,
      interest_tags: (data as any).interestTags ?? [],
      default_card_note: clamp(data.defaultCardNote, 500),
      ...(typeof resolvedIsPrimary === "boolean" ? { is_primary: resolvedIsPrimary } : {}),
    };

    const query = data.id
      ? supabase.from("recipients").update(payload).eq("id", data.id).eq("user_id", userId)
      : supabase.from("recipients").insert(payload);

    const { data: row, error } = await query.select(SELECT_COLS).single();

    if (error || !row) {
      console.error("[recipients] upsert failed", error);
      throw new Error("Failed to save recipient");
    }
    return row as RecipientRow;
  });

export const deleteRecipient = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { id: string }) => {
    if (!data?.id || typeof data.id !== "string") throw new Error("Invalid id");
    return data;
  })
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("recipients")
      .delete()
      .eq("id", data.id)
      .eq("user_id", userId);
    if (error) throw new Error("Failed to delete");
    return { ok: true };
  });
